import { validateUrl } from "@/common/validator/validate-url";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { Node } from "@tiptap/pm/model";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { File } from "buffer";
import { FileInput } from "flowbite-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { parseFileUrl } from "../../common/util/parse-file-url";
import { systemService } from "../../services/system-service";
import { BoldIcon } from "../icon/BoldIcon";
import { ImageIcon } from "../icon/ImageIcon";
import { ItalicIcon } from "../icon/ItalicIcon";
import { LinkIcon } from "../icon/LinkIcon";
import { StrikethroughIcon } from "../icon/StrikethroughIcon";
import { UnderlineIcon } from "../icon/UnderlineIcon";
import "./CustomEditor.css";

interface Props {
  content: string;
  handleContentChange: (content: string) => void;
  style?: React.CSSProperties;
}

export const CustomEditor: React.FC<Props> = ({
  content,
  handleContentChange: onContentChange,
  style,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  // 마지막 커서 위치를 저장하기 위한 상태
  const [lastSelection, setLastSelection] = useState<any>(null);

  let parsedContent;
  try {
    parsedContent =
      typeof content === "string"
        ? content.startsWith("{")
          ? JSON.parse(content)
          : {
              type: "doc",
              content: [
                {
                  type: "paragraph",
                  content: [
                    {
                      type: "text",
                      text: content,
                    },
                  ],
                },
              ],
            }
        : content || {
            type: "doc",
            content: [
              {
                type: "paragraph",
                content: [],
              },
            ],
          };
  } catch (error) {
    console.error("Invalid JSON content:", error);
    parsedContent = {
      type: "doc",
      content: [
        {
          type: "paragraph",
          content: [],
        },
      ],
    };
  }

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          class: "custom-link",
        },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: {
          class: "custom-image", // TODO 이미지 핸들링
        },
      }),
    ],
    content: "",
    onSelectionUpdate: ({ editor }) => {
      // 커서 위치 변경시 저장
      setLastSelection(editor.state.selection);
    },
    onUpdate: ({ editor, transaction }) => {
      const nodeIds = new Set<string>();
      transaction.doc.forEach((node) => {
        if (node.attrs.id) {
          nodeIds.add(node.attrs.id);
        }
      });
      transaction.before.forEach((node) => {
        if (node.attrs.id && !nodeIds.has(node.attrs.id)) {
          onNodeDeleted(node);
        }
      });
      const newContent = JSON.stringify(editor.getJSON());
      onContentChange(newContent);
      handleImageDeletes(transaction);
    },
  });

  // content 변경 시 에디터 내용 업데이트
  useEffect(() => {
    if (editor && content) {
      try {
        // content 파싱
        const parsedContent =
          typeof content === "string"
            ? content.startsWith("{")
              ? JSON.parse(content)
              : {
                  type: "doc",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: content,
                        },
                      ],
                    },
                  ],
                }
            : content;

        // 현재 에디터 내용과 새로운 content가 다를 때만 업데이트
        if (
          JSON.stringify(editor.getJSON()) !== JSON.stringify(parsedContent)
        ) {
          editor.commands.setContent(parsedContent);

          // 저장된 커서 위치가 있다면 복구
          if (lastSelection) {
            editor.commands.setTextSelection(lastSelection);
          }
        }
      } catch (error) {
        console.error("Error updating editor content:", error);
        editor.commands.setContent("");
      }
    }
  }, [editor, content, lastSelection]);

  // content가 변경될 때마다 에디터 내용 업데이트
  useEffect(() => {
    if (editor && content) {
      try {
        const parsedContent =
          typeof content === "string" ? JSON.parse(content) : content;

        // 현재 에디터 내용과 새로운 content가 다를 때만 업데이트
        if (
          JSON.stringify(editor.getJSON()) !== JSON.stringify(parsedContent)
        ) {
          editor.commands.setContent(parsedContent);

          // 저장된 커서 위치가 있다면 복구
          if (lastSelection) {
            editor.commands.setTextSelection(lastSelection);
          }
        }
      } catch (error) {
        console.error("Error updating editor content:", error);
      }
    }
  }, [content, editor, lastSelection]);

  const deleteImagesRef = useRef<string[]>([]); // 삭제된 이미지 저장

  const findDeletedElements = (A: string[], B: string[]) => {
    const setB = new Set(B);
    const deletedElements = A.filter((element) => !setB.has(element));
    return deletedElements;
  };

  const handleImageDeletes = async (transaction: any) => {
    const getImageSrcs = (content: any) => {
      const srcs: string[] = [];
      content.forEach((node: any) => {
        if (node.type.name === "image") {
          srcs.push(node.attrs.src);
        }
      });
      return srcs;
    };

    let currentSrcs = getImageSrcs(transaction.doc.content);
    let previousSrcs = getImageSrcs(transaction.before.content);

    let deletedSrcs = findDeletedElements(previousSrcs, currentSrcs);

    const tasks = deletedSrcs.map((src) => {
      const { folderName, fileName } = parseFileUrl(src);
      return systemService.deleteFile(folderName, fileName);
    });

    await Promise.all(tasks).then(() => {});
  };

  const onNodeDeleted = (node: Node) => {};

  const setLink = useCallback(() => {
    const previousUrl = editor?.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor?.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    if (!validateUrl(url)) {
      return alert("올바른 URL 형식이 아닙니다. 다시 입력해주세요.");
    }

    editor
      ?.chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  }, [editor]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  const ref = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (ref.current) {
      ref.current.click();
    }
  };

  const handleUpdateFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      const maxSize = 10 * 1024 * 1024;

      const validFiles = filesArray.filter(
        (file) => file.type.startsWith("image/") && file.size <= maxSize
      );

      if (validFiles.length !== filesArray.length) {
        alert("이미지 파일만 업로드 가능합니다. (최대 10MB)");
      }

      if (validFiles.length > 0) {
        const newFiles = [...files];
        // newFiles[currentIndex] = validFiles[0];

        setFiles(newFiles);
      }

      const response = await systemService.uploadFile(
        "test",
        uuidv4(),
        validFiles[0]
      );

      editor?.chain().focus().setImage({ src: response.data.url }).run(); // Insert the image with the given URL
    }
  };

  return (
    <div
      className="flex flex-col rounded-[8px] border-[1px] border-semantic-divider"
      style={style}
    >
      <FileInput
        id="dropzone-file"
        className="hidden"
        ref={ref}
        onChange={handleUpdateFileChange}
      />
      <div className="flex py-[12px] px-[20px] bg-grayscale-contrast-5">
        {editor && (
          <div className="flex gap-[15px]">
            <BoldIcon
              handleClick={() => editor.chain().focus().toggleBold().run()}
            />
            <ItalicIcon
              handleClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <StrikethroughIcon
              handleClick={() => editor.chain().focus().toggleStrike().run()}
            />
            <UnderlineIcon
              handleClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <LinkIcon handleClick={setLink} />
          </div>
        )}
        <div className="h-[16px] w-[1px] mx-[24px] bg-semantic-divider" />
        <ImageIcon handleClick={handleClick} />{" "}
      </div>
      <div className="custom-editor-wrapper">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};
